import { http } from "../../utils/config";

const templateAPI = {
  // Get all with pagination and search
  getTemplates: (params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = '',
      status = '',
      sort_by = 'created_at',
      sort_order = 'desc'
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
      ...(search && { search }),
      ...(status !== '' && { status })
    });

    return http.get(`/api/v1/fee-templates?${queryParams}`);
  },

  getTemplateAll: (params = {}) => {
    return http.get(`/api/v1/fee-templates/all`, { params });
  },

  // Get by ID
  getTemplateById: (id) => {
    return http.get(`/api/v1/fee-templates/${id}`);
  },

  // Create new
  createTemplate: (templateData) => {
    return http.post('/api/v1/fee-templates', templateData);
  },

  // Update
  updateTemplate: (id, templateData) => {
    return http.put(`/api/v1/fee-templates/${id}`, templateData);
  },

  // Delete
  deleteTemplate: (id) => {
    return http.delete(`/api/v1/fee-templates/${id}`);
  },

  // Update status
  updateTemplateStatus: (id, status) => {
    return http.patch(`/api/v1/fee-templates/${id}/status`, {
      status: status
    });
  },

  // Search customers with advanced filters
  searchTemplates: (filters) => {
    return http.post('/api/v1/fee-templates/search', filters);
  }
};

// Helper functions for handling API responses
export const templateHelpers = {
  // Extract data from API response
  extractTemplateData: (response) => {
    if (response.data && response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Extract paginated list from API response
  extractTemplateList: (response) => {
    if (response.data && response.data.success) {
      const data = response.data.data;
      return {
        templates: data.data || [],
        pagination: {
          current: data.current_page,
          pageSize: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to,
          lastPage: data.last_page,
          nextPageUrl: data.next_page_url,
          prevPageUrl: data.prev_page_url
        }
      };
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Format data for form submission
  formatTemplateData: (formData) => {
    return {
      name: formData.name?.trim(),
      description: formData.description?.trim(),
      status: formData.status ?? true,
    };
  },

  // Validate data before submission
  validateTemplateData: (templateData) => {
    const errors = {};

    if (!templateData.name?.trim()) {
      errors.name = 'Tên khách hàng không được để trống';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
};

// Error handling wrapper
export const withErrorHandling = (apiFunction) => {
  return async (...args) => {
    try {
      const response = await apiFunction(...args);
      return response;
    } catch (error) {
      console.error('API Error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data.message || 'Dữ liệu không hợp lệ');
          case 401:
            throw new Error('Bạn cần đăng nhập để thực hiện thao tác này');
          case 403:
            throw new Error('Bạn không có quyền thực hiện thao tác này');
          case 404:
            throw new Error('Không tìm thấy khách hàng');
          case 422:
            throw new Error(data.message || 'Dữ liệu không hợp lệ');
          case 500:
            throw new Error(data.message || "Lỗi server, vui lòng thử lại sau");
          default:
            throw new Error(data.message || 'Đã có lỗi xảy ra');
        }
      } else if (error.request) {
        // Network error
        throw new Error('Không thể kết nối đến server');
      } else {
        // Other error
        throw new Error(error.message || 'Đã có lỗi xảy ra');
      }
    }
  };
};

// Wrapped API functions with error handling
export const templateAPIWithErrorHandling = {
  getTemplates: withErrorHandling(templateAPI.getTemplates),
  getTemplateAll: withErrorHandling(templateAPI.getTemplateAll),
  getTemplateById: withErrorHandling(templateAPI.getTemplateById),
  createTemplate: withErrorHandling(templateAPI.createTemplate),
  updateTemplate: withErrorHandling(templateAPI.updateTemplate),
  deleteTemplate: withErrorHandling(templateAPI.deleteTemplate),
  updateTemplateStatus: withErrorHandling(templateAPI.updateTemplateStatus),
  searchTemplates: withErrorHandling(templateAPI.searchTemplates)
};

export default templateAPI;